// ui/equipmentUI.js
// Equipment panel UI - shows equipped gear with 11 slots

import { gameState } from '../game/gameState.js';
import { getItem, getItemName, getItemIcon, getItemColor } from '../data/items/index.js';
import { toastWarning, toastSuccess } from './toast.js';
import logger from '../utils/logger.js';
import { formatNumber, formatWithCommas } from '../utils/format.js';
import { getAvailableSpells, getAllSpells, formatSpellEffect, SPELL_ELEMENTS } from '../data/spells.js';
import { combatSkill } from '../skills/combat/combat.js';
import { openGlyphPouchModal } from '../skills/combat/combatUI.js';
import { SKILLS } from '../game/constants.js';
import { getLevelFromXP } from '../systems/experience.js';
import { buildEquipmentStatsTooltip } from './equipmentTooltip.js';

// Tier colors / getTierColor live in equipmentTooltip.js — the equipment panel
// now builds its tooltip via the shared buildEquipmentStatsTooltip().

// Equipment slot configuration
// 12 slots: head, neck, shoulders, body, weapon, offhand, back, hands, legs, ring, feet, ammo
// Layout:        [Head]
//          [Neck][Shoulder]
//       [Weapon][Body][Offhand]
//       [Hands][Legs][Back]
//       [Ring][Feet][Ammo]
const EQUIPMENT_SLOTS = [
    { id: 'head', name: 'Head', icon: '🎩', row: 1, col: 2 },
    { id: 'neck', name: 'Neck', icon: '📿', row: 2, col: 2 },
    { id: 'shoulders', name: 'Shoulders', icon: '🛡️', row: 2, col: 3 },
    { id: 'weapon', name: 'Main Hand', icon: '⚔️', row: 3, col: 1 },
    { id: 'body', name: 'Body', icon: '👕', row: 3, col: 2 },
    { id: 'offhand', name: 'Off Hand', icon: '🛡️', row: 3, col: 3 },
    { id: 'hands', name: 'Hands', icon: '🧤', row: 4, col: 1 },
    { id: 'legs', name: 'Legs', icon: '👖', row: 4, col: 2 },
    { id: 'back', name: 'Back', icon: '🎒', row: 4, col: 3 },
    { id: 'ring', name: 'Ring', icon: '💍', row: 4, col: 3 },
    { id: 'feet', name: 'Feet', icon: '👢', row: 5, col: 2 },
    { id: 'ammo', name: 'Ammo', icon: '🏹', row: 5, col: 3 },
];

// Current view state
let currentView = 'inventory'; // 'inventory' or 'equipment'

// Track last equipment state for change detection
let lastEquipmentHash = '';

/**
 * Get current view mode
 * @returns {string} 'inventory' or 'equipment'
 */
export function getCurrentView() {
    return currentView;
}

/**
 * Set current view mode
 * @param {string} view - 'inventory' or 'equipment'
 */
export function setCurrentView(view) {
    currentView = view;
    updateViewDisplay();
}

/**
 * Toggle between inventory and equipment views
 */
export function toggleView() {
    currentView = currentView === 'inventory' ? 'equipment' : 'inventory';
    updateViewDisplay();
}

/**
 * Update the display based on current view
 */
function updateViewDisplay() {
    const inventoryDiv = document.getElementById('inventory');
    const equipmentDiv = document.getElementById('equipment');
    const inventoryTab = document.getElementById('inventory-tab');
    const equipmentTab = document.getElementById('equipment-tab');

    if (!inventoryDiv || !equipmentDiv) return;

    if (currentView === 'inventory') {
        inventoryDiv.style.display = 'block';
        equipmentDiv.style.display = 'none';
        inventoryTab?.classList.add('active');
        equipmentTab?.classList.remove('active');
    } else {
        inventoryDiv.style.display = 'none';
        equipmentDiv.style.display = 'block';
        inventoryTab?.classList.remove('active');
        equipmentTab?.classList.add('active');
        renderEquipmentPanel();
    }
}

/**
 * Render a single equipment slot
 * @param {object} slotConfig - Slot configuration
 * @returns {string} HTML for the slot
 */
function renderEquipmentSlot(slotConfig) {
    const gear = gameState.getEquippedGear();
    const equippedItemId = gear[slotConfig.id];

    // Check if offhand is blocked by 2H weapon
    const isTwoHandedBlocked = slotConfig.id === 'offhand' && gameState.hasEquippedTwoHandedWeapon();

    if (equippedItemId) {
        const item = getItem(equippedItemId);
        const icon = item ? getItemIcon(equippedItemId) : '?';
        const color = getItemColor(equippedItemId);

        // Full stats tooltip via the shared builder — includes the set-bonus
        // breakdown and every stat type. showComparison=false: comparing an
        // equipped item against itself is moot.
        const tooltipBody = item ? buildEquipmentStatsTooltip(equippedItemId, false) : equippedItemId;

        // Ammo slot shows quantity
        if (slotConfig.id === 'ammo') {
            const ammo = gameState.getEquippedAmmo();
            const qtyDisplay = ammo.quantity >= 1000 ? formatNumber(ammo.quantity) : ammo.quantity;
            return `
                <div class="equipment-slot occupied ammo"
                     data-slot="${slotConfig.id}"
                     data-item-id="${equippedItemId}"
                     data-tooltip="${tooltipBody}\nQty: ${formatWithCommas(ammo.quantity)}\nClick to unequip"
                     style="grid-row: ${slotConfig.row}; grid-column: ${slotConfig.col};">
                    <div class="slot-content" style="background-color: ${color};">
                        <span class="item-icon">${icon}</span>
                        <span class="ammo-qty">${qtyDisplay}</span>
                    </div>
                    <span class="slot-label">${slotConfig.name}</span>
                </div>
            `;
        }

        return `
            <div class="equipment-slot occupied"
                 data-slot="${slotConfig.id}"
                 data-item-id="${equippedItemId}"
                 data-tooltip="${tooltipBody}\nClick to unequip"
                 style="grid-row: ${slotConfig.row}; grid-column: ${slotConfig.col};">
                <div class="slot-content" style="background-color: ${color};">
                    <span class="item-icon">${icon}</span>
                </div>
                <span class="slot-label">${slotConfig.name}</span>
            </div>
        `;
    }

    // Empty slot - check if blocked by 2H weapon
    if (isTwoHandedBlocked) {
        return `
            <div class="equipment-slot empty blocked"
                 data-slot="${slotConfig.id}"
                 data-tooltip="Blocked by two-handed weapon"
                 style="grid-row: ${slotConfig.row}; grid-column: ${slotConfig.col};">
                <div class="slot-content">
                    <span class="slot-icon">🔒</span>
                </div>
                <span class="slot-label">${slotConfig.name}</span>
            </div>
        `;
    }

    // Empty slot
    return `
        <div class="equipment-slot empty"
             data-slot="${slotConfig.id}"
             data-tooltip="${slotConfig.name} - Empty"
             style="grid-row: ${slotConfig.row}; grid-column: ${slotConfig.col};">
            <div class="slot-content">
                <span class="slot-icon">${slotConfig.icon}</span>
            </div>
            <span class="slot-label">${slotConfig.name}</span>
        </div>
    `;
}

/**
 * Render combat stats summary
 * @returns {string} HTML for stats summary
 */
function renderCombatStats() {
    const gear = gameState.getEquippedGear();

    // Calculate totals
    let totalAttack = 0;
    let totalDefense = 0;
    let meleeAccuracy = 0;
    let rangedAccuracy = 0;
    let magicAccuracy = 0;
    let weaponSpeed = 3000; // Base attack speed
    let attackStyle = 'None';

    for (const [slot, itemId] of Object.entries(gear)) {
        if (!itemId) continue;
        const item = getItem(itemId);
        if (!item) continue;

        totalAttack += item.attackBonus || 0;
        totalDefense += item.defenseBonus || 0;
        meleeAccuracy += item.meleeAccuracy || 0;
        rangedAccuracy += item.rangedAccuracy || 0;
        magicAccuracy += item.magicAccuracy || 0;

        if (slot === 'weapon') {
            if (item.attackSpeed) weaponSpeed = item.attackSpeed;
            if (item.attackStyle) attackStyle = item.attackStyle.charAt(0).toUpperCase() + item.attackStyle.slice(1);
        }
    }

    // Show accuracy for current attack style, or all if no weapon
    let accuracyDisplay = '';
    if (attackStyle === 'Melee' && meleeAccuracy > 0) {
        accuracyDisplay = `<div class="stat-row"><span class="stat-label">Melee Acc:</span><span class="stat-value positive">+${meleeAccuracy}</span></div>`;
    } else if (attackStyle === 'Ranged' && rangedAccuracy > 0) {
        accuracyDisplay = `<div class="stat-row"><span class="stat-label">Ranged Acc:</span><span class="stat-value positive">+${rangedAccuracy}</span></div>`;
    } else if (attackStyle === 'Magic' && magicAccuracy > 0) {
        accuracyDisplay = `<div class="stat-row"><span class="stat-label">Magic Acc:</span><span class="stat-value positive">+${magicAccuracy}</span></div>`;
    } else {
        // Show all accuracies if no weapon equipped or mixed
        if (meleeAccuracy > 0) accuracyDisplay += `<div class="stat-row"><span class="stat-label">Melee Acc:</span><span class="stat-value positive">+${meleeAccuracy}</span></div>`;
        if (rangedAccuracy > 0) accuracyDisplay += `<div class="stat-row"><span class="stat-label">Ranged Acc:</span><span class="stat-value positive">+${rangedAccuracy}</span></div>`;
        if (magicAccuracy > 0) accuracyDisplay += `<div class="stat-row"><span class="stat-label">Magic Acc:</span><span class="stat-value positive">+${magicAccuracy}</span></div>`;
    }

    // For magic style, show spell damage instead of attack
    let offenseRow = '';
    if (attackStyle === 'Magic') {
        const spellDamage = combatSkill.getTotalSpellDamage();
        offenseRow = `
            <div class="stat-row">
                <span class="stat-label">Spell Dmg:</span>
                <span class="stat-value positive">${spellDamage.toFixed(2)}x</span>
            </div>
        `;
    } else {
        offenseRow = `
            <div class="stat-row">
                <span class="stat-label">Attack:</span>
                <span class="stat-value ${totalAttack > 0 ? 'positive' : ''}">${totalAttack > 0 ? '+' : ''}${totalAttack}</span>
            </div>
        `;
    }

    return `
        <div class="equipment-stats">
            ${offenseRow}
            <div class="stat-row">
                <span class="stat-label">Defense:</span>
                <span class="stat-value ${totalDefense > 0 ? 'positive' : ''}">${totalDefense > 0 ? '+' : ''}${totalDefense}</span>
            </div>
            ${accuracyDisplay}
            <div class="stat-row">
                <span class="stat-label">Speed:</span>
                <span class="stat-value">${(weaponSpeed / 1000).toFixed(1)}s</span>
            </div>
            <div class="stat-row">
                <span class="stat-label">Style:</span>
                <span class="stat-value style-${attackStyle.toLowerCase()}">${attackStyle}</span>
            </div>
        </div>
    `;
}

// Track spell picker visibility
let spellPickerOpen = false;

/**
 * Render glyph pouch UI (6 slots for storing glyphs)
 * Shows under equipment grid for magic users
 */
function renderGlyphPouch() {
    const pouch = gameState.getGlyphPouch();
    const isUnlocked = gameState.isGlyphPouchUnlocked();

    // If locked, show locked state with unlock hint
    if (!isUnlocked) {
        return `
            <div class="glyph-pouch locked">
                <div class="glyph-pouch-header">
                    <span class="pouch-icon">🔒</span>
                    <span class="pouch-title">Glyph Pouch</span>
                </div>
                <div class="glyph-pouch-locked-message">
                    Unlock to store glyphs for quick spell switching
                </div>
            </div>
        `;
    }

    // Build slot HTML
    let slotsHtml = '';
    for (let i = 0; i < pouch.slots.length; i++) {
        const slot = pouch.slots[i];
        if (slot.itemId && slot.quantity > 0) {
            const icon = getItemIcon(slot.itemId);
            const name = getItemName(slot.itemId);
            const color = getItemColor(slot.itemId);
            const qtyDisplay = slot.quantity >= 1000 ? formatNumber(slot.quantity) : slot.quantity;
            slotsHtml += `
                <div class="glyph-pouch-slot occupied"
                     data-pouch-slot="${i}"
                     data-item-id="${slot.itemId}"
                     data-tooltip="${name}\nQty: ${formatWithCommas(slot.quantity)}\nClick to withdraw">
                    <div class="slot-content" style="background-color: ${color};">
                        <span class="item-icon">${icon}</span>
                        <span class="glyph-qty">${qtyDisplay}</span>
                    </div>
                </div>
            `;
        } else {
            slotsHtml += `
                <div class="glyph-pouch-slot empty"
                     data-pouch-slot="${i}"
                     data-tooltip="Empty slot">
                    <div class="slot-content">
                        <span class="slot-icon">◇</span>
                    </div>
                </div>
            `;
        }
    }

    // Build tooltip summary of contents
    const contents = pouch.slots.filter(s => s.itemId && s.quantity > 0);
    const totalGlyphs = contents.reduce((sum, s) => sum + s.quantity, 0);

    return `
        <div class="glyph-pouch clickable" onclick="window.equipmentUI.openGlyphPouchModal()" data-tooltip="Click to manage glyphs">
            <div class="glyph-pouch-header">
                <span class="pouch-icon">🎒</span>
                <span class="pouch-title">Glyph Pouch (${totalGlyphs})</span>
            </div>
            <div class="glyph-pouch-slots">
                ${slotsHtml}
            </div>
        </div>
    `;
}

/**
 * Render spell slot for magic equipment set (similar to ammo slot)
 */
function renderSpellSlot() {
    const state = combatSkill.getCombatState();
    const activeSpell = state.activeSpell;

    if (activeSpell) {
        // Build glyph cost string for tooltip
        const glyphCostText = activeSpell.glyphCost.map(cost => {
            const glyphName = getItemName(cost.glyphId);
            return `${glyphName} x${cost.quantity}`;
        }).join(', ');

        return `
            <div class="equipment-slot occupied spell-slot"
                 data-slot="spell"
                 data-tooltip="${activeSpell.name}\nDmg: ${activeSpell.baseDamage}\n${formatSpellEffect(activeSpell.effect)}\nCost: ${glyphCostText}\nClick to change"
                 style="grid-row: 5; grid-column: 3;">
                <div class="slot-content" style="background-color: ${activeSpell.color};">
                    <span class="item-icon">${activeSpell.icon}</span>
                </div>
                <span class="slot-label">Spell</span>
            </div>
        `;
    }

    // Empty spell slot
    return `
        <div class="equipment-slot empty spell-slot"
             data-slot="spell"
             data-tooltip="Spell - Click to select"
             style="grid-row: 5; grid-column: 3;">
            <div class="slot-content">
                <span class="slot-icon">🪄</span>
            </div>
            <span class="slot-label">Spell</span>
        </div>
    `;
}

/**
 * Render spell picker popup
 */
function renderSpellPicker() {
    if (!spellPickerOpen) return '';

    const combatXP = gameState.getSkill(SKILLS.COMBAT).xp;
    const combatLevel = getLevelFromXP(combatXP);
    const allSpells = getAllSpells();
    const state = combatSkill.getCombatState();
    const activeSpell = state.activeSpell;

    // Group spells by element
    const elements = Object.values(SPELL_ELEMENTS);
    const elementInfo = {
        fire: { name: 'Fire', icon: '🔥', color: '#FF4500' },
        ice: { name: 'Ice', icon: '❄️', color: '#87CEEB' },
        earth: { name: 'Earth', icon: '🪨', color: '#8B4513' },
        air: { name: 'Air', icon: '💨', color: '#9370DB' }
    };

    let spellListHtml = '';
    for (const element of elements) {
        const spells = allSpells.filter(s => s.element === element);
        const info = elementInfo[element];

        const spellItems = spells.map(spell => {
            const isLocked = combatLevel < spell.level;
            const isSelected = activeSpell && activeSpell.id === spell.id;

            // Check glyph availability
            let hasGlyphs = true;
            let glyphCostHtml = '';
            for (const cost of spell.glyphCost) {
                const totalCount = gameState.getTotalGlyphCount(cost.glyphId);
                const hasCost = totalCount >= cost.quantity;
                if (!hasCost) hasGlyphs = false;
                const glyphIcon = getItemIcon(cost.glyphId);
                glyphCostHtml += `<span class="spell-glyph ${hasCost ? '' : 'missing'}">${glyphIcon}${cost.quantity}</span>`;
            }

            const statusClass = isLocked ? 'locked' : (!hasGlyphs ? 'no-glyphs' : '');

            return `
                <div class="spell-option ${isSelected ? 'selected' : ''} ${statusClass}"
                     data-spell-id="${spell.id}"
                     data-tooltip="${spell.name}${isLocked ? ' (Lv ' + spell.level + ')' : ''}\nDmg: ${spell.baseDamage}\n${formatSpellEffect(spell.effect)}">
                    <span class="spell-icon">${spell.icon}</span>
                    <span class="spell-info">
                        <span class="spell-name">${spell.name}</span>
                        <span class="spell-cost">${glyphCostHtml}</span>
                    </span>
                    ${isLocked ? `<span class="spell-lock">🔒${spell.level}</span>` : ''}
                </div>
            `;
        }).join('');

        spellListHtml += `
            <div class="spell-element" data-element="${element}">
                <div class="element-label" style="color: ${info.color}">${info.icon} ${info.name}</div>
                <div class="spell-element-grid">
                    ${spellItems}
                </div>
            </div>
        `;
    }

    return `
        <div class="spell-picker-overlay" data-close-picker="true"></div>
        <div class="spell-picker">
            <div class="spell-picker-header">
                <span>Select Spell</span>
                <button class="spell-picker-close" data-close-picker="true">✕</button>
            </div>
            <div class="spell-picker-content">
                ${spellListHtml}
            </div>
        </div>
    `;
}

/**
 * Render equipment set switcher buttons
 */
function renderSetSwitcher() {
    const activeSet = gameState.getActiveEquipmentSet();
    const sets = gameState.getEquipmentSets();

    let buttonsHTML = '';
    for (const [setId, setData] of Object.entries(sets)) {
        const isActive = setId === activeSet;
        buttonsHTML += `
            <button class="set-button ${isActive ? 'active' : ''}"
                    data-set="${setId}"
                    data-tooltip="${setData.name} Set">
                ${setData.icon}
            </button>
        `;
    }

    return `
        <div class="equipment-set-switcher">
            ${buttonsHTML}
        </div>
    `;
}

/**
 * Render the full equipment panel
 */
export function renderEquipmentPanel() {
    const equipmentDiv = document.getElementById('equipment');
    if (!equipmentDiv) return;

    const activeSet = gameState.getActiveEquipmentSet();

    let slotsHTML = '';
    for (const slot of EQUIPMENT_SLOTS) {
        // Only show ammo slot for ranged set
        if (slot.id === 'ammo' && activeSet !== 'ranged') {
            continue;
        }
        // Only show back slot for ranged set
        if (slot.id === 'back' && activeSet !== 'ranged') {
            continue;
        }
        // Hide offhand slot for ranged set (ranged uses back/quiver instead)
        if (slot.id === 'offhand' && activeSet === 'ranged') {
            continue;
        }

        // For ranged set, reposition slots to match combat layout:
        // Back (quiver) at row 2, col 1 (left of neck)
        // Ammo (arrows) at row 2, col 3 — swapped up into the shoulders spot
        // Vambraces (the 'shoulders' slot) at row 3, col 3 — swapped down with ammo
        //   and relabeled 'Vambraces' for ranged. See armor.js ARMOR_SLOTS note.
        if (slot.id === 'ammo' && activeSet === 'ranged') {
            const rangedAmmoSlot = { ...slot, row: 2, col: 3 };
            slotsHTML += renderEquipmentSlot(rangedAmmoSlot);
            continue;
        }
        if (slot.id === 'shoulders' && activeSet === 'ranged') {
            const rangedVambracesSlot = { ...slot, row: 3, col: 3, name: 'Vambraces' };
            slotsHTML += renderEquipmentSlot(rangedVambracesSlot);
            continue;
        }
        if (slot.id === 'back' && activeSet === 'ranged') {
            const rangedBackSlot = { ...slot, row: 2, col: 1 };
            slotsHTML += renderEquipmentSlot(rangedBackSlot);
            continue;
        }

        slotsHTML += renderEquipmentSlot(slot);
    }

    // Spell slot for magic set (like ammo for ranged)
    if (activeSet === 'magic') {
        slotsHTML += renderSpellSlot();
    }

    // Glyph pouch for magic set
    const glyphPouchHtml = activeSet === 'magic' ? renderGlyphPouch() : '';

    equipmentDiv.innerHTML = `
        ${renderSetSwitcher()}
        <div class="equipment-grid">
            ${slotsHTML}
        </div>
        ${glyphPouchHtml}
        ${renderCombatStats()}
        ${renderSpellPicker()}
    `;

    // Update equipment hash after render
    const gear = gameState.getEquippedGear();
    const ammo = gameState.getEquippedAmmo();
    const activeSpell = combatSkill.getActiveSpell();
    const glyphPouch = gameState.getGlyphPouch();
    lastEquipmentHash = JSON.stringify({ gear, activeSet, ammoQty: ammo.quantity, activeSpellId: activeSpell?.id, glyphPouch: glyphPouch.slots });
}

/**
 * Update equipment display (called during game tick if visible)
 * Uses hash-based change detection to avoid unnecessary re-renders
 */
export function updateEquipmentDisplay() {
    if (currentView !== 'equipment') return;

    // Check if equipment has changed since last render
    const gear = gameState.getEquippedGear();
    const activeSet = gameState.getActiveEquipmentSet();
    const ammo = gameState.getEquippedAmmo();
    const activeSpell = combatSkill.getActiveSpell();
    const glyphPouch = gameState.getGlyphPouch();
    const currentHash = JSON.stringify({ gear, activeSet, ammoQty: ammo.quantity, activeSpellId: activeSpell?.id, glyphPouch: glyphPouch.slots });

    if (currentHash !== lastEquipmentHash) {
        renderEquipmentPanel();
    }
}

/**
 * Handle clicking on an equipment slot
 * @param {Event} e - Click event
 */
function handleEquipmentClick(e) {
    // Check for set button click first
    const setButton = e.target.closest('.set-button');
    if (setButton) {
        const setId = setButton.dataset.set;
        if (setId) {
            gameState.loadEquipmentSet(setId);
            spellPickerOpen = false; // Close picker when switching sets
            renderEquipmentPanel();
            logger.debug(`[EQUIPMENT] Switched to ${setId} set`);
        }
        return;
    }

    // Check for spell picker close
    const closePicker = e.target.closest('[data-close-picker]');
    if (closePicker) {
        spellPickerOpen = false;
        renderEquipmentPanel();
        return;
    }

    // Check for spell selection click in picker
    const spellOption = e.target.closest('.spell-option');
    if (spellOption) {
        const spellId = spellOption.dataset.spellId;
        if (spellId && !spellOption.classList.contains('locked')) {
            const result = combatSkill.selectSpell(spellId);
            if (result.success) {
                toastSuccess(`Selected ${result.spell.name}`);
                logger.debug(`[EQUIPMENT] Selected spell ${spellId}`);
                spellPickerOpen = false;
                renderEquipmentPanel();
            } else {
                toastWarning(result.reason || 'Could not select spell');
            }
        }
        return;
    }

    // Check for spell slot click (open picker)
    const spellSlot = e.target.closest('.spell-slot');
    if (spellSlot) {
        spellPickerOpen = !spellPickerOpen;
        renderEquipmentPanel();
        return;
    }

    // Glyph pouch now uses modal - ignore clicks on pouch/slots (parent onclick handles it)
    if (e.target.closest('.glyph-pouch')) {
        return;
    }

    const slot = e.target.closest('.equipment-slot');
    if (!slot) return;

    const slotId = slot.dataset.slot;
    const itemId = slot.dataset.itemId;

    if (!itemId) {
        // Empty slot - could show equippable items in future
        logger.debug(`[EQUIPMENT] Clicked empty ${slotId} slot`);
        return;
    }

    // Unequip the item
    const result = gameState.unequipItem(slotId);
    if (result.success) {
        combatSkill.invalidateSetBonusCache();
        const item = getItem(itemId);
        const name = item ? item.name : itemId;
        toastSuccess(`Unequipped ${name}`);
        logger.debug(`[EQUIPMENT] Unequipped ${itemId} from ${slotId}`);
        renderEquipmentPanel();
    } else {
        toastWarning(result.reason || 'Could not unequip item');
    }
}

/**
 * Initialize equipment UI
 */
export function initEquipmentUI() {
    const equipmentDiv = document.getElementById('equipment');
    if (equipmentDiv) {
        equipmentDiv.addEventListener('click', handleEquipmentClick);
    }

    // Setup tab click handlers
    const inventoryTab = document.getElementById('inventory-tab');
    const equipmentTab = document.getElementById('equipment-tab');

    if (inventoryTab) {
        inventoryTab.addEventListener('click', () => setCurrentView('inventory'));
    }
    if (equipmentTab) {
        equipmentTab.addEventListener('click', () => setCurrentView('equipment'));
    }

    // Set initial view
    updateViewDisplay();

    logger.debug('[EQUIPMENT] Equipment UI initialized');
}

// Expose functions to window for onclick handlers
if (typeof window !== 'undefined') {
    window.equipmentUI = {
        openGlyphPouchModal
    };
}

export default {
    getCurrentView,
    setCurrentView,
    toggleView,
    renderEquipmentPanel,
    updateEquipmentDisplay,
    initEquipmentUI,
    EQUIPMENT_SLOTS,
};
